/**
 * RoleDetailsModal Component
 *
 * Modal for viewing global role details including permissions and assignments.
 * Shows who (users/groups) has been assigned this role.
 *
 * Global Roles Architecture:
 * - Roles are tenant-wide (not application-scoped)
 * - Permissions format: ResourceScopedPermission[] with { actions: string[], resources: string[] }
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useRole } from '../hooks/useRole';
import { formatDate } from '../../../lib/utils/format';
import type { RoleSanitized, ResourceScopedPermission } from '../../../types/role.types';
import { normalizePermissions } from '../../../types/role.types';

interface RoleDetailsModalProps {
  role: RoleSanitized | null;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const RoleDetailsModal: React.FC<RoleDetailsModalProps> = ({
  role,
  tenantId,
  isOpen,
  onClose,
}) => {
  // Fetch fresh role data with enriched details
  const { data: roleDetails, isLoading } = useRole(tenantId, role?.roleId || null);

  const currentRole = roleDetails || role;

  if (!currentRole) return null;

  // Normalize permissions to handle both legacy and new format
  const normalizedPermissions = normalizePermissions(currentRole.permissions as any);

  // Format ResourceScopedPermission for display
  const formatPermissionGroup = (permission: ResourceScopedPermission, index: number) => {
    const actionsDisplay = permission.actions.length === 1 && permission.actions[0] === '*'
      ? 'All Actions (*)'
      : permission.actions.join(', ');

    const resourceDisplay = permission.resources.length === 1 && permission.resources[0] === '*'
      ? 'All Resources (*)'
      : permission.resources.join(', ');

    return (
      <div key={index} className="bg-white rounded-md p-3 border border-gray-200">
        <div className="space-y-2">
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Actions:</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {permission.actions.map((action, actionIndex) => (
                <Badge key={actionIndex} variant={action === '*' ? 'warning' : 'success'} className="text-xs font-mono">
                  {action}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Resources:</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {permission.resources.map((resource, resourceIndex) => (
                <Badge key={resourceIndex} variant={resource === '*' ? 'warning' : 'info'} className="text-xs font-mono">
                  {resource}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Count assignments
  const userCount = currentRole.users?.length || 0;
  const groupCount = currentRole.groups?.length || 0;
  const totalAssignments = userCount + groupCount;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Role Details</Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
            <p className="mt-4 text-gray-500">Loading role details...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Role Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{currentRole.roleName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Role Code</dt>
                  <dd className="mt-1">
                    <CopyableCode value={currentRole.roleCode} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Role ID</dt>
                  <dd className="mt-1">
                    <CopyableCode value={currentRole.roleId} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Role Type</dt>
                  <dd className="mt-1">
                    <Badge variant={currentRole.type === 'tenant' ? 'warning' : 'info'}>
                      {currentRole.type}
                    </Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Path</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{currentRole.path || '/'}</dd>
                </div>
                {currentRole.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{currentRole.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Permission Groups ({normalizedPermissions.length})
              </h3>
              {normalizedPermissions.length > 0 ? (
                <div className="space-y-3">
                  {normalizedPermissions.map((permission, index) => formatPermissionGroup(permission, index))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No permissions defined</p>
              )}
            </div>

            {/* Assignments */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Assignments ({totalAssignments})
              </h3>
              {totalAssignments > 0 ? (
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {userCount > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">
                        Assigned Users ({userCount})
                      </dt>
                      <dd className="mt-1 flex flex-wrap gap-2">
                        {currentRole.users.map((user) => (
                          <Badge key={user.userId} variant="success">
                            {user.email || `${user.userId.slice(0, 8)}...`}
                          </Badge>
                        ))}
                      </dd>
                    </div>
                  )}
                  {groupCount > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">
                        Assigned Groups ({groupCount})
                      </dt>
                      <dd className="mt-1 flex flex-wrap gap-2">
                        {currentRole.groups.map((group) => (
                          <Badge key={group.groupId} variant="success">
                            {group.groupCode || `${group.groupId.slice(0, 8)}...`}
                          </Badge>
                        ))}
                      </dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  This role has not been assigned to any users or groups yet.
                </p>
              )}
            </div>

            {/* Metadata */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(currentRole.createdAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(currentRole.updatedAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
